import { computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export enum Theme {
  light = 'light',
  dark = 'dark'
}

export const useTheme = defineStore(
  'theme',
  () => {
    const theme = ref<Theme>(getSavedTheme() || getMediaPreference());
    const documentElement = document.documentElement;

    function getSavedTheme() {
      const ls = localStorage.getItem('theme');
      if (ls !== null) {
        try {
          const parsed = JSON.parse(ls);
          if (parsed && parsed.theme) {
            return parsed.theme;
          }
        } catch (e) {
          return '';
        }
      }
      return getMediaPreference();
    }

    function getMediaPreference() {
      const hasDarkPreference = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (hasDarkPreference) {
        return Theme.dark;
      } else {
        return Theme.light;
      }
    }

    watch(theme, () => {
      if (theme.value !== Theme.dark && theme.value !== Theme.light) {
        setTheme(getMediaPreference());
      }
    });

    function setTheme(newTheme: Theme) {
      theme.value = newTheme;
      documentElement.setAttribute('data-bs-theme', newTheme === Theme.dark ? 'dark' : 'light');
    }

    function setLight() {
      setTheme(Theme.light);
    }

    function setDark() {
      setTheme(Theme.dark);
    }

    const isDark = computed(() => theme.value === Theme.dark);
    const isLight = computed(() => theme.value === Theme.light);

    setTheme(theme.value);

    return { theme, isDark, isLight, setDark, setLight, setTheme };
  },
  {
    persist: true
  }
);
